



























































import { Component, Mixins, Emit, Watch } from "vue-property-decorator";
import { MovableTextBoxItem, IDrawableItem } from "../../../../domain/document/items";
import {
  IConfiguration,
} from "../../../../domain/configuration";
import { IObjectStyleReadable } from "../IObjectStyleReadable";
import { MovableTextObjectStyleReader } from "../styleReaders";
import {
  StyledTextEditableObjectMixin,
  DataValidatableMixin,
  ImeControllableMixin,
  ExternalTemplateInputtableMixin,
} from "./mixins";
import { DragKinds } from "../../../dragging/DragKinds";
import { DragParameter } from "../../../dragging/DragParameter";
import { Point } from "../../../../domain/drawing";
import TextBoxDragHandles from "../../../dragging/TextBoxDragHandles.vue";
import { IKeyboardEventHandleable } from "..";
import { KeyCodeProcessor } from "./KeyCodeProcessor";
import { MovableTextBoxInputMethodKinds } from "../../../../domain/document/inputMethodKinds";
import { MovableTextBoxInputMethodOptionKinds } from "../../../../domain/document/inputMethodOptionKinds";
import { IFreeInputableObject } from "./IFreeInputableObject";
import { IItemCopyableObject } from "./IItemCopyableObject";
import { ImelessTextarea } from "../../../system";

@Component({
  components: {
    TextBoxDragHandles,
    ImelessTextarea,
  },
})
export default class MovableTextBoxObject extends Mixins(
  StyledTextEditableObjectMixin,
  DataValidatableMixin,
  ImeControllableMixin,
  ExternalTemplateInputtableMixin,
) implements IKeyboardEventHandleable, IFreeInputableObject, IItemCopyableObject {
  mounted(): void {
    this.updateInputElement(this.getInputableElement());
  }

  getInputableElement(): HTMLInputElement {
    return !!this.$refs.imelessTextarea
      ? (this.$refs.imelessTextarea as ImelessTextarea).getEditor() as unknown as HTMLInputElement
      : this.$refs.textElement as HTMLInputElement;
  }

  get editStyleReader(): IObjectStyleReadable {
    const configuration = this.$store.getters.configuration as IConfiguration;
    return new MovableTextObjectStyleReader(configuration.style.document.editor.objects.movableTextBox);
  }

  editingSource: MovableTextBoxItem;

  data(): any {
    return {
      isInputting: false,
    };
  }
  isInputting: boolean;

  get titleWithLengthDescription(): string {
    return this.appendLengthDescription(this.title, this.editingSource);
  }

  get title(): string {
    if (this.toolTip) {
      return this.toolTip;
    }
    return this.editingSource.name;
  }

  get freeInputText(): string {
    return this.editingSource.value;
  }

  set freeInputText(value: string) {
    this.writeProperty((s: MovableTextBoxItem) => {
      const newValue = this.validateText(this.editingSource, value);
      if (this.isFreeInputable && this.isFreeInputOverwrittenByAll && this.isFocused && s.value !== newValue) {
        // 「全てフリー入力」の状態で手入力された場合はフリー入力に切り替える
        s.shouldInputFree = true;
      }
      s.value = newValue;
    });
  }

  get freeInputMeasureText(): string {
    this.updateTextPositionHeight();
    return this.textPositionCompositing
      ? this.textPositionMeasureText
      : this.freeInputText;
  }

  get inputMethodKind(): MovableTextBoxInputMethodKinds {
    return this.editingSource.inputMethodKind;
  }

  set inputMethodKind(value: MovableTextBoxInputMethodKinds) {
    this.writeProperty(s => {
      s.inputMethodKind = value;
      if (value !== MovableTextBoxInputMethodKinds.MKSTemplate) {
        s.mksTemplateRegisteredId = null;
      }
      if (value !== MovableTextBoxInputMethodKinds.DynamicTemplate) {
        s.dynamicTemplateRegisteredId = null;
      }
    });
  }

  get inputMethodOptionKind(): MovableTextBoxInputMethodOptionKinds {
    return this.editingSource.inputMethodOptionKind;
  }

  setFocus(): void {
    // ポップアップメニューからフリー入力に切り替えるとfocusが当たるが、そのまま色などを変更すると、
    // データ選択に戻ってしまうので対策している
    this.$nextTick(() => {
      this.emitFocus();
    });
  }

  get isInputableText(): boolean {
    return this.isFreeInputable && this.shouldInputFreeSelfOrAll;
  }

  get textareaTabindex(): number {
    return this.isInputableText
      ? 0
      : -1;
  }
  
  get shouldPrint(): boolean {
    return this.editingSource.printBehavior.shouldPrint;
  }

  @Emit()
  dragHandleSelected(source: IDrawableItem, parameter: DragParameter): void {}

  onDragHandleSelected(event: PointerEvent, kind: DragKinds): void {
    this.emitFocus(); // focusが当たってないこともありえるのでfocus当てる。
    this.$nextTick(() => {
      // textareaのblurのあとに実行したいので遅らせている
      const rect = (this.$refs.objectContainer as HTMLDivElement).getBoundingClientRect();
      const parameter = new DragParameter(
        kind,
        new Point(event.clientX - rect.left, event.clientY - rect.top));
      this.dragHandleSelected(this.editingSource, parameter);
      // textElementから入力フォーカスは外す.(deleteキー対応.)
      this.getInputableElement().blur();
    });
  }

  onFocus(): void {
    this.isInputting = true;
    this.emitFocus();
  }

  onBlur(): void {
    this.isInputting = false;
    this.adjustTextFontSize();
  }

  adjustTextFontSize(): void {
    if ((!this.editingSource.isEmpty || this.editingSource.textStyle.isAdjustedFontSize) && this.isInputableText) {
      // MKSテンプレートもフォーカスを外した時に自動調整したほうが操作感が統一されてよさそうだが、
      // ダイアログを表示するとそこでフォーカスが外れるため、現状では入力後に自動調整している
      this.autoAdjustFontSizeWithRetry();
    }
  }

  handleKeyboardEvent(event: KeyboardEvent): boolean {
    if (this.isInputting) {
      return false;
    }
    const moveOffset = KeyCodeProcessor.toMoveOffset(event.keyCode);
    if (moveOffset) {
      this.writeProperty(s => s.geometry.rectangle = s.geometry.rectangle.move(moveOffset));
      return true;
    }
    return false;
  }

  @Watch("inputMethodKind")
  updatedInputMethodKind(newValue: MovableTextBoxInputMethodKinds, oldValue: MovableTextBoxInputMethodKinds): void {
    if (newValue !== oldValue) {
      // IEはいったんinputからフォーカスを外さないとreadonlyの制御が反映されない模様
      // 暫定対策で、他の要素にフォーカスを当てている
      this.$nextTick(() => {
        const element = this.getInputableElement();
        if (element == null) { return; }
        element.blur();
        element.focus();
        this.showDialogForSpecificObject();
      });
    }
  }

  @Watch("shouldInputFreeSelfOrAll")
  onShouldInputFreeChanged(newValue: boolean, oldValue: boolean): void {
    if (!this.isFreeInputable) { return; }
    if (!this.isFocused) { return; }
    if (!newValue) { return; }
    // IEでは、いったんマウスクリックさせるかフォーカス外さないとreadonlyの切り替えが反映されない模様
    this.$nextTick(() => {
      const element = this.getInputableElement();
      if (element == null) { return; }
      element.blur();
      element.focus();
    });
  }

  showDialogForSpecificObject(): void {
    if (!this.shouldInputFreeSelfOrAll) {
      this.inputExternalTemplateWhenNeeded();
      return;
    }
  }

  updateInputElement(value: HTMLInputElement): void {
    this.inputableElement = value;
  }

  pasteText(value: string): void {
    this.insertTextToCaretPosition(value);
    this.$nextTick(() => {
      this.adjustTextFontSize();
    });
  }

  overwriteText(value: string, isForcibly?: boolean): void {
    console.error("overwriteTextは実装されていません。");
  }

  get isSourceEmpty(): boolean {
    return !this.editingSource || this.editingSource.isEmpty;
  }
}
